import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../firebase/config'; 
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { isValidAvatarUrl } from '../../utils/avatarData';
import toast from 'react-hot-toast';

export default function CRMUsers({ allUsers = [], setSelectedProfile, isOwner }) {
    const containerRef = useRef(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState(''); 
    const [phone, setPhone] = useState('');
    const [pin, setPin] = useState('');
    const [role, setRole] = useState('STUDENT');
    const [generatedCredentials, setGeneratedCredentials] = useState(null);

    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, user: null });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editData, setEditData] = useState({ id: '', name: '', phone: '', pin: '', role: '' });

    useEffect(() => {
        const handleClick = () => setContextMenu({ ...contextMenu, visible: false });
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [contextMenu]);

    // 🔥 AUTO-GENERATE CLEAN USERNAME
    useEffect(() => {
        if(firstName || lastName) {
            const cleanFirst = firstName.trim().toLowerCase().replace(/[^a-z]/g, '');
            const cleanLast = lastName.trim().toLowerCase().replace(/[^a-z]/g, '');
            setUsername(`${cleanFirst}${cleanLast}`);
        } else {
            setUsername('');
        }
    }, [firstName, lastName]);

    // 🚀 LIVE USERNAME AVAILABILITY CHECKER
    const cleanUsernameForCheck = username.trim().toLowerCase().replace(/\s+/g, '');
    const isUsernameTaken = allUsers.some(u => u.email === `${cleanUsernameForCheck}@hayaa.com`);

    const handleCreateProfile = async (e) => {
        e.preventDefault();
        if (!firstName || !lastName || !phone || !pin || !username) return toast.error("Fill all fields!");
        if (pin.length < 6) return toast.error("Password must be at least 6 characters!");
        if (isUsernameTaken) return toast.error("This username is already taken!");

        let finalRole = role;
        if (!isOwner && (role === 'OWNER' || role === 'ADMIN')) {
            finalRole = 'STUDENT';
            toast.error("You don't have permission to create Admins!");
        }

        const newEmail = `${cleanUsernameForCheck}@hayaa.com`;
        const loadingId = toast.loading("Creating Profile & Auth Account...");

        try {
            const secondaryApp = getApps().length > 1 ? getApp("SecondaryApp") : initializeApp(auth.app.options, "SecondaryApp");
            const secondaryAuth = getAuth(secondaryApp);
            
            await createUserWithEmailAndPassword(secondaryAuth, newEmail, pin);
            await secondaryAuth.signOut(); 

            await setDoc(doc(db, "users", newEmail), {
                name: `${firstName.trim()} ${lastName.trim()}`,
                username: cleanUsernameForCheck, 
                email: newEmail,
                phone: phone,
                pin: pin,
                role: finalRole,
                avatar: finalRole === 'COACH' ? '👨‍🏫' : (finalRole === 'ADMIN' ? '🛡️' : '🎓'),
                level: 1,
                totalXp: 0,
                classCredits: finalRole === 'STUDENT' ? 0 : null,
                createdAt: new Date().toISOString()
            });

            setGeneratedCredentials({
                name: `${firstName} ${lastName}`,
                username: cleanUsernameForCheck,
                pin: pin,
                role: finalRole
            });

            toast.success("Profile & Login Created!", { id: loadingId });
            setFirstName(''); setLastName(''); setUsername(''); setPhone(''); setPin('');
        } catch (error) {
            console.error("Creation Error:", error);
            if (error.code === 'auth/email-already-in-use') {
                toast.error("This username is already taken in system!", { id: loadingId });
            } else {
                toast.error("Failed to create profile.", { id: loadingId });
            }
        }
    };

    const handleContextMenu = (e, user) => {
        e.preventDefault(); 
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        
        let clickX = e.clientX - rect.left;
        let clickY = e.clientY - rect.top;
        
        const menuWidth = 220; 
        const menuHeight = 150; 
        if (clickX + menuWidth > rect.width) clickX = rect.width - menuWidth - 15;
        if (clickY + menuHeight > rect.height) clickY = rect.height - menuHeight - 15;
        if (clickX < 0) clickX = 10;
        if (clickY < 0) clickY = 10;
        
        setContextMenu({ visible: true, x: clickX, y: clickY, user: user });
    };

    const openEditModal = (user) => {
        setEditData({ id: user.id || user.email || Math.random().toString(), name: user.name || '', phone: user.phone || '', pin: user.pin || '', role: user.role || 'STUDENT' });
        setIsEditModalOpen(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        if (!isOwner && (editData.role === 'OWNER' || editData.role === 'ADMIN') && contextMenu.user.role !== editData.role) {
            return toast.error("You cannot promote users to Admin/Owner.");
        }
        const loadingId = toast.loading("Updating Profile...");
        try {
            await updateDoc(doc(db, "users", editData.id), {
                name: editData.name,
                phone: editData.phone,
                pin: editData.pin,
                role: editData.role,
                avatar: editData.role === 'COACH' ? '👨‍🏫' : (editData.role === 'ADMIN' ? '🛡️' : '🎓')
            });
            toast.success("Database Profile Updated!", { id: loadingId });
            setIsEditModalOpen(false);
        } catch (error) {
            toast.error("Failed to update profile.", { id: loadingId });
        }
    };

    const handleDeleteUser = async (user) => {
        const docId = user.id || user.email;
        if (window.confirm(`🚨 Are you absolutely sure you want to DELETE ${user.name || 'this user'}?\nThis action cannot be undone.`)) {
            const loadingId = toast.loading("Deleting Profile...");
            try {
                await deleteDoc(doc(db, "users", docId));
                toast.success(`${user.name || 'User'} deleted from DB.`, { id: loadingId });
            } catch (error) {
                toast.error("Failed to delete user.", { id: loadingId });
            }
        }
    };

    return (
        <div ref={containerRef} style={{display: 'flex', flexDirection: 'column', gap: '30px', position: 'relative' }}>
            
            <div style={{background: 'var(--bg-primary)', padding: '25px', borderRadius: '16px', }}>
                <h2 style={{color: 'var(--text-primary)', marginTop: 0, marginBottom: '20px' }}>🌟 Create Account (Auth Linked)</h2>
                <form onSubmit={handleCreateProfile} style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Account Type</label>
                        <select value={role} onChange={e => setRole(e.target.value)} style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                            <option value="STUDENT">🎓 Student</option>
                            <option value="COACH">👨‍🏫 Coach</option>
                            {isOwner && <option value="ADMIN">🛡️ Admin Manager</option>}
                        </select>
                    </div>
                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '5px' }}>First Name</label>
                        <input type="text" placeholder="e.g. Aarav" value={firstName} onChange={e => setFirstName(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }} />
                    </div>
                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Last Name</label>
                        <input type="text" placeholder="e.g. Sharma" value={lastName} onChange={e => setLastName(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }} />
                    </div>
                    
                    {/* 🔥 SMART USERNAME BOX (REAL-TIME CHECK) */}
                    <div>
                        <label style={{color: isUsernameTaken ? '#ef4444' : '#10b981', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                            {isUsernameTaken ? '⚠️ Username Taken' : '✅ Login Username'}
                        </label>
                        <input 
                            type="text" 
                            value={username} 
                            onChange={e => setUsername(e.target.value)} 
                            required 
                            style={{width: '100%', padding: '12px', borderRadius: '8px', 
                                background: isUsernameTaken ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)', 
                                color: isUsernameTaken ? '#ef4444' : '#10b981', 
                                border: isUsernameTaken ? '1px solid #ef4444' : '1px solid #10b981',
                                outline: 'none',
                                transition: '0.3s'
                            }} 
                        />
                        {isUsernameTaken && (
                            <div style={{color: '#ef4444', fontSize: '11px', marginTop: '5px', fontWeight: 'bold' }}>
                                Try adding a number (e.g. {cleanUsernameForCheck}1)
                            </div>
                        )}
                    </div>

                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '5px' }}>WhatsApp Number</label>
                        <input type="tel" placeholder="9876543210" value={phone} onChange={e => setPhone(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }} />
                    </div>
                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Secure Password</label>
                        <input type="text" placeholder="e.g. chess123" value={pin} onChange={e => setPin(e.target.value)} minLength="6" maxLength="20" required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }} />
                    </div>
                    
                    <div style={{gridColumn: '1 / -1', marginTop: '10px' }}>
                        {/* 🔥 DISABLE BUTTON IF USERNAME IS TAKEN */}
                        <button type="submit" disabled={isUsernameTaken} style={{padding: '12px 25px', background: isUsernameTaken ? '#475569' : '#3b82f6', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: isUsernameTaken ? 'not-allowed' : 'pointer', width: '100%', transition: '0.3s', opacity: isUsernameTaken ? 0.6 : 1 }}>
                            {isUsernameTaken ? 'Change Username to Create' : 'Create Secure Account'}
                        </button>
                    </div>
                </form>

                {generatedCredentials && (
                    <div style={{marginTop: '20px', background: 'var(--bg-primary)', padding: '15px', borderRadius: '8px', border: '1px dashed #10b981' }}>
                        <div style={{color: '#10b981', fontWeight: 'bold', marginBottom: '10px' }}>✅ Account & Login Created!</div>
                        <div style={{background: 'var(--bg-primary)', padding: '10px', borderRadius: '6px', color: 'var(--text-secondary)', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`Welcome to Hayaa Chess Academy! ♟️
Name: ${generatedCredentials.name}
Role: ${generatedCredentials.role}
Username: ${generatedCredentials.username}
Password: ${generatedCredentials.pin}`}
                        </div>
                    </div>
                )}
            </div>

            <div style={{background: 'var(--bg-primary)', padding: '25px', borderRadius: '16px' }}>
                <h2 style={{color: 'var(--text-primary)', marginTop: 0 }}>Registered Users (Right-Click to Manage)</h2>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    {allUsers.map((u, index) => {
                        // 🔥 THE FIX: EXTREMELY SAFE FALLBACKS
                        const safeEmail = u.email || '';
                        const safeUsername = u.username || (safeEmail.includes('@') ? safeEmail.split('@')[0] : 'unknown_user');
                        const safeName = u.name || 'No Name';
                        const safeAvatar = u.avatar || '👤';
                        const uniqueKey = u.id || safeEmail || index.toString();

                        return (
                            <div 
                                key={uniqueKey} 
                                onClick={() => setSelectedProfile(u)} 
                                onContextMenu={(e) => handleContextMenu(e, u)} 
                                style={{
                                    background: 'var(--bg-primary)', 
                                    padding: '16px', 
                                    borderRadius: '16px', 
                                    cursor: 'pointer',
                                    boxShadow: 'var(--neu-shadow)',
                                    border: '1.5px solid rgba(255, 255, 255, 0.95)',
                                    transition: 'all 0.2s ease-in-out',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'var(--neu-shadow)';
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '38px',
                                        height: '38px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        background: u.role === 'COACH' ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' : (u.role === 'ADMIN' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #eff6ff, #dbeafe)'),
                                        boxShadow: 'inset 2px 2px 4px rgba(255, 255, 255, 0.7), inset -2px -2px 4px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(163, 177, 198, 0.15)',
                                        border: '1px solid rgba(255, 255, 255, 0.5)',
                                        flexShrink: 0,
                                        overflow: 'hidden'
                                    }}>
                                        {isValidAvatarUrl(safeAvatar) ? (
                                            <img src={safeAvatar} alt={safeName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                                        ) : (
                                            safeAvatar
                                        )}
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ color: 'var(--text-primary)', fontWeight: '800', fontSize: '14px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{safeName}</div>
                                        <div style={{ color: '#10b981', fontSize: '12px', fontWeight: 'bold' }}>@{safeUsername}</div>
                                    </div>
                                </div>
                                
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px', 
                                    background: 'var(--bg-primary)', 
                                    padding: '6px 12px', 
                                    borderRadius: '10px',
                                    boxShadow: 'var(--neu-shadow-inset)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)' 
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>
                                        Pin: <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: '800' }}>{u.pin || 'None'}</span>
                                    </span>
                                </div>

                                <div>
                                    <div style={{
                                        display: 'inline-block',
                                        padding: '3px 8px',
                                        borderRadius: '6px',
                                        fontSize: '9px',
                                        fontWeight: '900',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        color: u.role === 'COACH' ? '#d97706' : (u.role === 'ADMIN' ? '#059669' : '#2563eb'),
                                        background: u.role === 'COACH' ? 'rgba(245, 158, 11, 0.08)' : (u.role === 'ADMIN' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(59, 130, 246, 0.08)'),
                                        border: u.role === 'COACH' ? '1px solid rgba(245, 158, 11, 0.15)' : (u.role === 'ADMIN' ? '1px solid rgba(16, 185, 129, 0.15)' : '1px solid rgba(59, 130, 246, 0.15)')
                                    }}>
                                        {u.role || 'STUDENT'}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {contextMenu.visible && (
                <div style={{position: 'absolute', top: contextMenu.y, left: contextMenu.x, background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)', padding: '8px 0', zIndex: 9999, minWidth: '180px' }}>
                    <div style={{padding: '8px 15px', color: 'var(--text-secondary)', fontSize: '12px', borderBottom: '1px solid var(--bg-primary)', marginBottom: '4px' }}>
                        Managing: <strong style={{color: 'var(--text-primary)'}}>{contextMenu.user?.name || 'User'}</strong>
                    </div>
                    
                    <button onClick={() => { openEditModal(contextMenu.user); setContextMenu({ ...contextMenu, visible: false }); }} style={{width: '100%', textAlign: 'left', padding: '10px 15px', background: 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s ease' }} onMouseEnter={e => e.target.style.background = 'var(--btn-hover)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                        ✏️ Edit Details
                    </button>

                    <button onClick={() => { setSelectedProfile(contextMenu.user); setContextMenu({ ...contextMenu, visible: false }); }} style={{width: '100%', textAlign: 'left', padding: '10px 15px', background: 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s ease' }} onMouseEnter={e => e.target.style.background = 'var(--btn-hover)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                        👁️ View Analytics
                    </button>

                    {isOwner && (
                        <button onClick={() => { handleDeleteUser(contextMenu.user); setContextMenu({ ...contextMenu, visible: false }); }} style={{width: '100%', textAlign: 'left', padding: '10px 15px', background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: '14px', marginTop: '4px', transition: 'all 0.15s ease' }} onMouseEnter={e => e.target.style.background = 'var(--btn-hover)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                            🗑️ Delete User
                        </button>
                    )}
                </div>
            )}

            {isEditModalOpen && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
                    <div style={{background: 'var(--bg-primary)', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)' }}>
                        <h2 style={{color: 'var(--text-primary)', marginTop: 0, marginBottom: '20px' }}>✏️ Edit Profile</h2>
                        
                        <form onSubmit={handleUpdateUser} style={{display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px' }}>Full Name</label>
                                <input type="text" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', marginTop: '5px' }} />
                            </div>
                            
                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px' }}>WhatsApp Number</label>
                                <input type="tel" value={editData.phone} onChange={e => setEditData({...editData, phone: e.target.value})} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', marginTop: '5px' }} />
                            </div>

                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px' }}>Current Login Password</label>
                                <input type="text" value={editData.pin} readOnly style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'rgba(0, 0, 0, 0.05)', color: 'var(--text-secondary)', border: '1px solid rgba(163, 177, 198, 0.2)', marginTop: '5px', cursor: 'not-allowed' }} />
                            </div>

                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px' }}>Role</label>
                                <select value={editData.role} onChange={e => setEditData({...editData, role: e.target.value})} style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', marginTop: '5px' }}>
                                    <option value="STUDENT">🎓 Student</option>
                                    <option value="COACH">👨‍🏫 Coach</option>
                                    {isOwner && <option value="ADMIN">🛡️ Admin Manager</option>}
                                </select>
                            </div>

                            <p style={{color: '#ef4444', fontSize: '11px', textAlign: 'center', marginTop: '10px' }}>
                                ⚠️ Note: To change a user's Login Password, you will need to delete and recreate their account or use Firebase Console.
                            </p>

                            <div style={{display: 'flex', gap: '10px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setIsEditModalOpen(false)} style={{flex: 1, padding: '12px', background: 'rgba(163, 177, 198, 0.4)', color: 'var(--text-primary)', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
                                <button type="submit" style={{flex: 1, padding: '12px', background: '#10b981', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
